(function(root) {
  "use strict";

  const namespace = root.LegitGrails = root.LegitGrails || {};
  const consent = namespace.cookieConsent;
  const adKeys = /^\$?(?:initial_)?(gclid|dclid|gbraid|wbraid|gad_source|gclsrc|msclkid|fbclid|ttclid|rdt_cid|oppref|lg_clid|lg_clid_kind|lg_gcl|click_id)$/i;
  const serialKeys = /^(serial|serial_number|code|typed_code|checker_code)$/i;
  const flagListeners = [];
  const eventDestinations = [];
  let posthogInstance = null;
  let flagsUnsubscribe = null;
  let flagsReady = false;
  let formTouched = false;

  function allowed(category) {
    const state = consent.getState();
    return state.isRegionResolved && state.effectiveCategories[category] === true;
  }

  function safeUrl(input) {
    try {
      const url = new URL(input, root.location.href);
      Array.from(url.searchParams.keys()).forEach(function(key) {
        if (serialKeys.test(key) || (!allowed("advertisement") && adKeys.test(key))) url.searchParams.delete(key);
      });
      url.hash = "";
      return url.href;
    } catch (error) { return ""; }
  }

  function sanitize(input) {
    if (Array.isArray(input)) return input.map(sanitize);
    if (!input || typeof input !== "object") return input;
    const output = {};
    Object.keys(input).forEach(function(key) {
      if (serialKeys.test(key) || (!allowed("advertisement") && adKeys.test(key))) return;
      const item = input[key];
      if (typeof item === "string" && /^(https?:\/\/|\/)/.test(item)) {
        const cleaned = safeUrl(item);
        // Preserve legacy relative cta_path/landing dimensions, not absolute URLs.
        if (item.charAt(0) === "/" && item.charAt(1) !== "/") {
          try { const url = new URL(cleaned); output[key] = url.pathname + url.search; } catch (error) { output[key] = ""; }
        } else output[key] = cleaned;
      } else output[key] = sanitize(item);
    });
    return output;
  }

  function trafficSource() {
    if (namespace.attribution) return namespace.attribution.trafficSource();
    try {
      const stored = JSON.parse(root.localStorage.getItem("utm_data") || "null");
      if (!stored || !stored.timestamp || Date.now() - stored.timestamp > 6 * 30 * 86400000) return {};
      const data = stored.data || {};
      const source = {};
      ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "utm_id", "gclid"].forEach(function(key) {
        if (data[key] && (key !== "gclid" || allowed("advertisement"))) source[key] = data[key];
      });
      return Object.keys(source).length ? { traffic_source: source } : {};
    } catch (error) { return {}; }
  }

  /** PostHog-only theme events; SDK automatic events share the before_send policy. */
  function capture(name, properties) {
    if (!allowed("analytics") || !posthogInstance) return false;
    try {
      posthogInstance.capture(name, sanitize(Object.assign({}, properties || {}, trafficSource())));
      return true;
    } catch (error) { return false; }
  }

  /**
   * Registers an event recipient independently from its SDK lifecycle adapter.
   * Only Google uses canSend to express the existing Advanced exception. Other
   * destinations require their category and must recheck permission after awaits.
   * @param {{id: string, category: string, send: function(string, object): *, canSend?: function(): boolean}} destination
   * @returns {function(): void} Unregister callback.
   */
  function registerDestination(destination) {
    if (!destination || !destination.id || typeof destination.send !== "function") throw new Error("Invalid event destination");
    if (eventDestinations.some(function(item) { return item.id === destination.id; })) throw new Error("Duplicate event destination: " + destination.id);
    eventDestinations.push(destination);
    return function() {
      const index = eventDestinations.indexOf(destination);
      if (index !== -1) eventDestinations.splice(index, 1);
    };
  }

  /** Broadcasts only to permitted destinations, with a fresh payload per recipient. */
  function track(name, properties) {
    let accepted = false;
    eventDestinations.slice().forEach(function(destination) {
      try {
        if (!(destination.canSend ? destination.canSend() : allowed(destination.category))) return;
        const data = sanitize(Object.assign({}, properties || {}, allowed("analytics") ? trafficSource() : {}));
        const result = destination.send(name, data);
        if (result && typeof result.then === "function") result.catch(function() {});
        if (result !== false) accepted = true;
      } catch (error) {}
    });
    return accepted;
  }

  function notifyFlags() {
    if (!allowed("analytics") || !flagsReady || formTouched) return;
    flagListeners.slice().forEach(function(callback) {
      if (!allowed("analytics") || formTouched) return;
      try { callback(); } catch (error) {}
    });
  }

  function setPosthog(instance) {
    if (typeof flagsUnsubscribe === "function") flagsUnsubscribe();
    flagsUnsubscribe = null;
    posthogInstance = instance;
    flagsReady = false;
    if (instance && typeof instance.onFeatureFlags === "function") {
      flagsUnsubscribe = instance.onFeatureFlags(function() {
        if (!allowed("analytics")) return;
        flagsReady = true;
        notifyFlags();
      });
    }
  }

  function onFeatureFlags(callback) {
    if (typeof callback !== "function" || formTouched) return function() {};
    flagListeners.push(callback);
    if (flagsReady && allowed("analytics")) {
      try { callback(); } catch (error) {}
    }
    return function() {
      const index = flagListeners.indexOf(callback);
      if (index !== -1) flagListeners.splice(index, 1);
    };
  }

  ["click", "change", "input"].forEach(function(name) {
    document.addEventListener(name, function(event) {
      if (event.target && event.target.closest && event.target.closest(".product-form, [data-product-form], .authenticate-btn, #authenticate-btn")) formTouched = true;
    }, true);
  });
  root.addEventListener("pageshow", function(event) {
    // Preserve the browser-restored form/variant on bfcache, even with late flags.
    if (event.persisted) formTouched = true;
  });

  namespace.analytics = {
    allowed: allowed,
    safeUrl: safeUrl,
    sanitize: sanitize,
    trafficSource: trafficSource,
    capture: capture,
    track: track,
    registerDestination: registerDestination,
    setPosthog: setPosthog,
    onFeatureFlags: onFeatureFlags,
    getFeatureFlag: function(key) {
      if (!allowed("analytics") || !posthogInstance || !flagsReady) return undefined;
      try { return posthogInstance.getFeatureFlag(key); } catch (error) { return undefined; }
    }
  };
})(window);
